import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchPolicies } from '../../store/slices/policySlice';
import { Link } from 'react-router-dom';
import {
    FileText,
    Search,
    Filter,
    ChevronRight,
    Shield,
    Calendar,
    DollarSign,
    Ship,
    CheckCircle,
    Clock,
    AlertCircle,
    TrendingUp,
    BarChart3,
    Users,
    Building,
    MapPin,
    Plus,
    Loader2,
    Eye
} from 'lucide-react';
import Badge from '../../components/ui/Badge';

export default function PolicyList() {
    const dispatch = useDispatch();
    const { items, loading, error } = useSelector((state) => state.policies);
    const [statusFilter, setStatusFilter] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');
    const [sortBy, setSortBy] = useState('newest');
    const [viewMode, setViewMode] = useState('list');

    useEffect(() => {
        dispatch(fetchPolicies());
    }, [dispatch]);

    // Filter and sort policies
    const filteredPolicies = items
        ?.filter((p) => (statusFilter === 'all' ? true : p.payment_status === statusFilter))
        ?.filter(
            (p) =>
                p.policy_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                p.vessel_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                p.insured_name?.toLowerCase().includes(searchTerm.toLowerCase())
        )
        ?.sort((a, b) => {
            switch (sortBy) {
                case 'newest':
                    return new Date(b.issued_date) - new Date(a.issued_date);
                case 'oldest':
                    return new Date(a.issued_date) - new Date(b.issued_date);
                case 'value_high':
                    return (b.sum_insured || 0) - (a.sum_insured || 0);
                case 'value_low':
                    return (a.sum_insured || 0) - (b.sum_insured || 0);
                default:
                    return 0;
            }
        }) || [];

    // Calculate statistics
    const stats = {
        total: items?.length || 0,
        active: items?.filter(p => p.payment_status === 'paid').length || 0,
        pending: items?.filter(p => p.payment_status === 'unpaid').length || 0,
        totalValue: items?.reduce((sum, p) => sum + Number(p.sum_insured || 0), 0) || 0,
        averageValue: items?.length ? (items.reduce((sum, p) => sum + Number(p.sum_insured || 0), 0) / items.length) : 0,
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'paid': return 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 border-green-200 dark:border-green-800';
            case 'unpaid': return 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 border-amber-200 dark:border-amber-800';
            default: return 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-400 border-gray-200 dark:border-gray-600';
        }
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'paid': return <CheckCircle className="w-4 h-4" />;
            case 'unpaid': return <Clock className="w-4 h-4" />;
            default: return <AlertCircle className="w-4 h-4" />;
        }
    };

    const formatCurrency = (value, currency = 'USD') => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: currency || 'USD',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        }).format(value || 0);
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-slate-900 dark:to-slate-800">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
                {/* Header */}
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                    <div>
                        <div className="flex items-center gap-3">
                            <div className="p-2 bg-[#3a663f]/10 dark:bg-[#3a663f]/20 rounded-lg">
                                <Shield className="w-6 h-6 text-[#3a663f] dark:text-green-400" />
                            </div>
                            <div>
                                <h1 className="text-3xl font-extrabold text-gray-800 dark:text-white">Policies</h1>
                                <p className="text-gray-500 dark:text-slate-400 text-sm mt-0.5">
                                    {filteredPolicies.length} policy{filteredPolicies.length !== 1 ? 's' : ''} found
                                    {items && items.length !== filteredPolicies.length && (
                                        <span className="ml-2 text-xs text-gray-400">
                                            (Showing {filteredPolicies.length} of {items.length})
                                        </span>
                                    )}
                                </p>
                            </div>
                        </div>
                    </div>
                    <Link
                        to="/cargo"
                        className="inline-flex items-center gap-2 bg-[#3a663f] hover:bg-[#2d5231] text-white font-semibold py-3 px-6 rounded-xl transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                    >
                        <Plus className="w-5 h-5" />
                        New Cargo Certificate
                    </Link>
                </div>

                {/* Statistics Cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <StatCard
                        label="Total Policies"
                        value={stats.total}
                        icon={BarChart3}
                        color="bg-[#3a663f]"
                    />
                    <StatCard
                        label="Active"
                        value={stats.active}
                        icon={CheckCircle}
                        color="bg-green-500"
                    />
                    <StatCard
                        label="Pending"
                        value={stats.pending}
                        icon={Clock}
                        color="bg-amber-500"
                    />
                    <StatCard
                        label="Total Value"
                        value={formatCurrency(stats.totalValue)}
                        icon={DollarSign}
                        color="bg-blue-500"
                    />
                </div>

                {/* Filters and Search Bar */}
                <div className="bg-white dark:bg-slate-800 rounded-xl shadow-lg border border-gray-200 dark:border-slate-700 p-4">
                    <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                        {/* Status Tabs */}
                        <div className="flex flex-wrap gap-1">
                            {[
                                { key: 'all', label: 'All', icon: BarChart3 },
                                { key: 'paid', label: 'Active', icon: CheckCircle },
                                { key: 'unpaid', label: 'Pending', icon: Clock },
                            ].map((tab) => {
                                const Icon = tab.icon;
                                const isActive = statusFilter === tab.key;
                                const count = tab.key === 'all'
                                    ? stats.total
                                    : tab.key === 'paid'
                                        ? stats.active
                                        : stats.pending;
                                return (
                                    <button
                                        key={tab.key}
                                        onClick={() => setStatusFilter(tab.key)}
                                        className={`flex items-center gap-1.5 px-3.5 py-2 text-sm font-medium rounded-lg transition-all ${isActive
                                                ? 'bg-[#3a663f] text-white shadow-lg'
                                                : 'text-gray-500 dark:text-slate-400 hover:bg-gray-100 dark:hover:bg-slate-700'
                                            }`}
                                    >
                                        <Icon className="w-4 h-4" />
                                        {tab.label}
                                        <span className={`ml-1 text-xs ${isActive ? 'bg-white/20' : 'bg-gray-200 dark:bg-slate-600'
                                            } px-1.5 py-0.5 rounded-full`}>
                                            {count}
                                        </span>
                                    </button>
                                );
                            })}
                        </div>

                        <div className="flex-1 flex items-center gap-3">
                            {/* Search */}
                            <div className="flex-1 relative">
                                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                                <input
                                    type="text"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    placeholder="Search by policy number, vessel, or insured..."
                                    className="w-full pl-10 pr-4 py-2 bg-gray-50 dark:bg-slate-700/50 border border-gray-200 dark:border-slate-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#3a663f] focus:border-transparent transition-all"
                                />
                            </div>

                            {/* Sort */}
                            <select
                                value={sortBy}
                                onChange={(e) => setSortBy(e.target.value)}
                                className="px-4 py-2 bg-gray-50 dark:bg-slate-700/50 border border-gray-200 dark:border-slate-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#3a663f] focus:border-transparent transition-all"
                            >
                                <option value="newest">Newest First</option>
                                <option value="oldest">Oldest First</option>
                                <option value="value_high">Highest Value</option>
                                <option value="value_low">Lowest Value</option>
                            </select>

                            {/* Refresh */}
                            <button
                                onClick={() => dispatch(fetchPolicies())}
                                className="p-2 text-gray-500 hover:text-[#3a663f] dark:text-slate-400 dark:hover:text-green-400 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors"
                                disabled={loading}
                            >
                                <Loader2 className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
                            </button>
                        </div>
                    </div>
                </div>

                {/* Loading State */}
                {loading && (
                    <div className="space-y-4">
                        {[1, 2, 3].map((i) => (
                            <div key={i} className="bg-white dark:bg-slate-800 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-slate-700 animate-pulse">
                                <div className="flex items-center justify-between">
                                    <div className="space-y-3 flex-1">
                                        <div className="h-5 bg-gray-200 dark:bg-slate-700 rounded w-1/4" />
                                        <div className="flex gap-4">
                                            <div className="h-4 bg-gray-100 dark:bg-slate-700 rounded w-1/6" />
                                            <div className="h-4 bg-gray-100 dark:bg-slate-700 rounded w-1/6" />
                                            <div className="h-4 bg-gray-100 dark:bg-slate-700 rounded w-1/6" />
                                        </div>
                                    </div>
                                    <div className="h-8 bg-gray-200 dark:bg-slate-700 rounded-full w-20" />
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {/* Error State */}
                {error && !loading && (
                    <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-8 text-center">
                        <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-3" />
                        <p className="text-red-600 dark:text-red-400 font-medium">{error}</p>
                        <button
                            onClick={() => dispatch(fetchPolicies())}
                            className="mt-3 text-sm text-red-700 dark:text-red-400 font-medium hover:underline flex items-center justify-center gap-1"
                        >
                            <Loader2 className="w-4 h-4" />
                            Try again
                        </button>
                    </div>
                )}

                {/* Empty State */}
                {!loading && !error && filteredPolicies.length === 0 && (
                    <div className="text-center py-20 bg-white dark:bg-slate-800 rounded-2xl shadow-sm border border-gray-200 dark:border-slate-700">
                        <div className="inline-flex items-center justify-center w-24 h-24 rounded-full bg-gray-100 dark:bg-slate-700 mb-6">
                            <Shield className="w-12 h-12 text-gray-300 dark:text-slate-500" />
                        </div>
                        <h3 className="text-xl font-bold text-gray-800 dark:text-white mb-2">
                            {searchTerm || statusFilter !== 'all' ? 'No matching policies' : 'No policies yet'}
                        </h3>
                        <p className="text-gray-500 dark:text-slate-400 max-w-sm mx-auto mb-6">
                            {searchTerm || statusFilter !== 'all'
                                ? 'Try adjusting your search or filters.'
                                : 'Create a cargo certificate to generate your first policy.'}
                        </p>
                        {!searchTerm && statusFilter === 'all' && (
                            <Link
                                to="/cargo"
                                className="inline-flex items-center gap-2 bg-[#3a663f] hover:bg-[#2d5231] text-white font-semibold py-3 px-6 rounded-xl transition-all shadow-lg hover:shadow-xl"
                            >
                                <Plus className="w-5 h-5" />
                                Create Cargo Certificate
                            </Link>
                        )}
                        {searchTerm && (
                            <button
                                onClick={() => setSearchTerm('')}
                                className="text-[#3a663f] dark:text-green-400 font-medium hover:underline"
                            >
                                Clear search
                            </button>
                        )}
                    </div>
                )}

                {/* Policies List */}
                {!loading && !error && filteredPolicies.length > 0 && (
                    <div className="space-y-4">
                        {/* Results count */}
                        <div className="flex items-center justify-between text-sm">
                            <span className="text-gray-500 dark:text-slate-400">
                                Showing <strong className="text-gray-700 dark:text-white">{filteredPolicies.length}</strong> of <strong className="text-gray-700 dark:text-white">{items?.length || 0}</strong> policies
                            </span>
                            <span className="text-gray-400 dark:text-slate-500">
                                Total Value: {formatCurrency(stats.totalValue)}
                            </span>
                        </div>

                        <div className="space-y-3">
                            {filteredPolicies.map((policy) => (
                                <Link
                                    key={policy.id}
                                    to={`/policies/${policy.id}`}
                                    className="group block bg-white dark:bg-slate-800 rounded-xl p-5 shadow-sm border border-gray-200 dark:border-slate-700 hover:shadow-lg hover:border-[#3a663f]/30 dark:hover:border-green-400/30 transition-all duration-300"
                                >
                                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                                        {/* Left: Policy Info */}
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-3 mb-2 flex-wrap">
                                                <h3 className="font-bold text-gray-800 dark:text-white group-hover:text-[#3a663f] dark:group-hover:text-green-400 transition-colors text-lg">
                                                    {policy.policy_number}
                                                </h3>
                                                <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium border ${getStatusColor(policy.payment_status)}`}>
                                                    {getStatusIcon(policy.payment_status)}
                                                    {policy.payment_status === 'paid' ? 'Active' : 'Pending'}
                                                </span>
                                                {policy.claim_count > 0 && (
                                                    <span className="text-xs bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 px-2 py-0.5 rounded-full">
                                                        {policy.claim_count} claim{policy.claim_count !== 1 ? 's' : ''}
                                                    </span>
                                                )}
                                            </div>
                                            <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-1 text-sm text-gray-500 dark:text-slate-400">
                                                <span className="flex items-center gap-1.5">
                                                    <Ship className="w-4 h-4 text-[#3a663f] dark:text-green-400" />
                                                    {policy.vessel_name || 'N/A'}
                                                </span>
                                                <span className="flex items-center gap-1.5">
                                                    <Calendar className="w-4 h-4 text-[#3a663f] dark:text-green-400" />
                                                    {new Date(policy.issued_date).toLocaleDateString()}
                                                </span>
                                                <span className="flex items-center gap-1.5">
                                                    <DollarSign className="w-4 h-4 text-[#3a663f] dark:text-green-400" />
                                                    {formatCurrency(policy.sum_insured)}
                                                </span>
                                                <span className="flex items-center gap-1.5">
                                                    <Users className="w-4 h-4 text-[#3a663f] dark:text-green-400" />
                                                    {policy.insured_name || 'N/A'}
                                                </span>
                                            </div>
                                            {policy.comments_conditions && (
                                                <p className="text-xs text-gray-400 dark:text-slate-500 mt-1.5 truncate max-w-md">
                                                    {policy.comments_conditions}
                                                </p>
                                            )}
                                        </div>

                                        {/* Right: Actions */}
                                        <div className="flex items-center gap-3 flex-shrink-0">
                                            <span className="text-sm text-[#3a663f] dark:text-green-400 font-medium opacity-0 group-hover:opacity-100 transition-all duration-300 flex items-center gap-1">
                                                View Details
                                                <ChevronRight className="w-4 h-4 transform group-hover:translate-x-1 transition-transform" />
                                            </span>
                                            <div className="w-8 h-8 rounded-full bg-[#3a663f]/10 dark:bg-[#3a663f]/20 flex items-center justify-center group-hover:bg-[#3a663f] transition-colors">
                                                <Eye className="w-4 h-4 text-[#3a663f] dark:text-green-400 group-hover:text-white transition-colors" />
                                            </div>
                                        </div>
                                    </div>

                                    {/* Progress Bar for payment status */}
                                    {policy.payment_status === 'unpaid' && (
                                        <div className="mt-3 pt-3 border-t border-gray-100 dark:border-slate-700">
                                            <div className="flex items-center justify-between text-xs mb-1">
                                                <span className="text-gray-500 dark:text-slate-400">Payment Status</span>
                                                <span className="text-amber-600 dark:text-amber-400 font-medium">Pending</span>
                                            </div>
                                            <div className="h-1.5 bg-gray-200 dark:bg-slate-700 rounded-full overflow-hidden">
                                                <div className="h-full bg-amber-500 rounded-full" style={{ width: '30%' }} />
                                            </div>
                                        </div>
                                    )}
                                </Link>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

// ========== SUB-COMPONENTS ==========

function StatCard({ label, value, icon: Icon, color }) {
    return (
        <div className="bg-white dark:bg-slate-800 rounded-xl p-4 shadow-sm border border-gray-200 dark:border-slate-700 hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between">
                <div>
                    <p className="text-xs text-gray-500 dark:text-slate-400 uppercase tracking-wider">{label}</p>
                    <p className="text-2xl font-bold text-gray-800 dark:text-white mt-0.5 truncate">{value}</p>
                </div>
                <div className={`p-2.5 rounded-lg ${color} text-white`}>
                    <Icon className="w-4 h-4" />
                </div>
            </div>
        </div>
    );
}